import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined, DeleteOutlined, SaveOutlined, StopOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  COMPETENCY_PERMS as P,
  useCompetencyAction,
  useCompetencyCan,
  useCompetencyOverview,
  useJobProfiles,
  useTrainingCourses,
  useTrainingSchedule,
  useTrainingSession,
} from '@/hooks/useCompetency';
import { competencyService, type AttendeeSelector, type TrainingSessionAttendee } from '@/services/competency.service';
import departmentService from '@/services/department.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_TRAINING_SESSION } from '@/constants/document-entity-types';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, STATUS_COLOR, employeeLabel } from './competencyDisplay';

const { Text } = Typography;

const TrainingSessionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const canManage = useCompetencyCan(P.trainingManage);
  const [form] = Form.useForm();
  const trainerType = Form.useWatch('trainer_type', form);
  const courseId = Form.useWatch('course_id', form);
  const sessionDate = Form.useWatch('session_date', form);
  const [selector, setSelector] = useState<AttendeeSelector>({});
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [completing, setCompleting] = useState(false);
  const [observations, setObservations] = useState('');

  const { data: session, isLoading } = useTrainingSession(isNew ? undefined : id);
  const { data: courses = [] } = useTrainingCourses({ active: '1' });
  const { data: employees = [] } = useCompetencyOverview();
  const { data: profiles = [] } = useJobProfiles();
  const { data: departments = [] } = useQuery({ queryKey: ['departments', 'selection'], queryFn: () => departmentService.getForSelection(), staleTime: 300_000 });
  const planYear = (sessionDate ? dayjs(sessionDate) : session ? dayjs(session.session_date) : dayjs()).year();
  const effectiveCourse = courseId ?? session?.course_id;
  const { data: schedule } = useTrainingSchedule(planYear, effectiveCourse ? { course_id: effectiveCourse } : undefined);
  const openItems = useMemo(() => (schedule?.items ?? []).filter((i) => i.status === 'Planned' && i.course_id === effectiveCourse), [schedule, effectiveCourse]);

  useEffect(() => {
    if (!session) return;
    form.setFieldsValue({ ...session, session_date: dayjs(session.session_date) });
    setMarks(Object.fromEntries((session.attendees ?? []).map((a) => [a.id, a.attendance_status ?? ''])));
    setObservations(session.trainer_observations ?? '');
  }, [session, form]);

  const editable = canManage && (isNew || session?.status === 'Planned');

  const saveHeader = useCompetencyAction(async (v: Record<string, unknown>) => {
    if (isNew) return competencyService.createSession({ ...v, ...selector });
    return competencyService.updateSession(id as string, v);
  }, 'Session saved');
  const addAttendees = useCompetencyAction((sel: AttendeeSelector) => competencyService.addAttendees(id as string, sel), 'Attendees added');
  const removeAttendee = useCompetencyAction((recordId: string) => competencyService.removeAttendee(id as string, recordId), 'Attendee removed');
  const saveAttendance = useCompetencyAction(
    (m: Array<{ id: string; attendance_status: string }>) => competencyService.setAttendance(id as string, m),
    'Attendance saved'
  );
  const complete = useCompetencyAction((obs: string) => competencyService.completeSession(id as string, obs || null), 'Session completed');
  const cancel = useCompetencyAction(() => competencyService.cancelSession(id as string), 'Session cancelled');

  if (!isNew && isLoading) return <Spin style={{ margin: 48 }} />;
  if (!isNew && !session) return <Alert type="error" showIcon style={{ margin: 24 }} message="Training session not found" />;

  const attendees = session?.attendees ?? [];
  const dirtyMarks = attendees.filter((a) => (marks[a.id] ?? '') !== (a.attendance_status ?? '') && marks[a.id]);
  const selectorCount =
    (selector.employee_ids?.length ?? 0) + (selector.department_ids?.length ?? 0) + (selector.job_profile_ids?.length ?? 0) + (selector.plan_item_ids?.length ?? 0);

  const onSave = async (v: Record<string, any>) => {
    const saved = await saveHeader.mutateAsync({ ...v, session_date: v.session_date.format('YYYY-MM-DD') });
    if (isNew && (saved as { id?: string })?.id) {
      setSelector({});
      navigate(`${COMPETENCY_BASE}/sessions/${(saved as { id: string }).id}`, { replace: true });
    }
  };

  const attendeeColumns: ColumnsType<TrainingSessionAttendee> = [
    { title: 'Emp. No.', dataIndex: 'employee_number', width: 110 },
    { title: 'Attendee name', dataIndex: 'full_name', render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/employees/${r.employee_id}`)}>{v}</a> },
    { title: 'Designation', dataIndex: 'job_title' },
    { title: 'Department', dataIndex: 'department_name' },
    {
      title: 'Attendance',
      width: 260,
      render: (_, r) =>
        session?.status === 'Planned' && canManage ? (
          <Radio.Group
            size="small"
            value={marks[r.id] || undefined}
            onChange={(e) => setMarks((m) => ({ ...m, [r.id]: e.target.value }))}
            options={['Present', 'Absent', 'Excused'].map((s) => ({ value: s, label: s }))}
            optionType="button"
          />
        ) : r.attendance_status ? (
          <Tag color={STATUS_COLOR[r.attendance_status]}>{r.attendance_status}</Tag>
        ) : (
          '-'
        ),
    },
    { title: 'Result', dataIndex: 'result', width: 90 },
    { title: 'Schedule', dataIndex: 'plan_item_id', width: 90, render: (v) => (v ? <Tag color="blue">Linked</Tag> : '-') },
    {
      title: '',
      width: 50,
      render: (_, r) =>
        session?.status === 'Planned' && canManage ? (
          <Popconfirm title="Remove this attendee?" onConfirm={() => removeAttendee.mutate(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ) : null,
    },
  ];

  const attendeePicker = (
    <Row gutter={12}>
      <Col xs={24} md={12}>
        <Text type="secondary">Employees</Text>
        <Select
          mode="multiple"
          showSearch
          style={{ width: '100%' }}
          placeholder="Pick employees"
          value={selector.employee_ids}
          onChange={(v) => setSelector((s) => ({ ...s, employee_ids: v }))}
          optionFilterProp="label"
          maxTagCount="responsive"
          options={employees.map((e) => ({ value: e.id, label: `${employeeLabel(e)}${e.department_name ? ` (${e.department_name})` : ''}` }))}
        />
      </Col>
      <Col xs={24} md={12}>
        <Text type="secondary">Scheduled for this course in {planYear}</Text>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder={openItems.length ? 'Pick schedule lines' : 'No open schedule lines'}
          disabled={!openItems.length}
          value={selector.plan_item_ids}
          onChange={(v) => setSelector((s) => ({ ...s, plan_item_ids: v }))}
          optionFilterProp="label"
          maxTagCount="responsive"
          options={openItems.map((i) => ({
            value: i.id,
            label: i.employee_id ? employeeLabel({ employee_number: i.employee_number, full_name: i.employee_name }) : `Dept: ${i.department_name}`,
          }))}
        />
      </Col>
      <Col xs={24} md={12} style={{ marginTop: 8 }}>
        <Text type="secondary">Whole departments</Text>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Pick departments"
          value={selector.department_ids}
          onChange={(v) => setSelector((s) => ({ ...s, department_ids: v }))}
          optionFilterProp="label"
          options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
        />
      </Col>
      <Col xs={24} md={12} style={{ marginTop: 8 }}>
        <Text type="secondary">Everyone with a job description</Text>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Pick job descriptions"
          value={selector.job_profile_ids}
          onChange={(v) => setSelector((s) => ({ ...s, job_profile_ids: v }))}
          optionFilterProp="label"
          options={profiles.map((p) => ({ value: p.id, label: `${p.code} - ${p.title}` }))}
        />
      </Col>
    </Row>
  );

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title={isNew ? 'New training session' : `${session?.session_number} - ${session?.course_title}`}
        extra={
          !isNew && session ? (
            <Space wrap>
              <Tag color={STATUS_COLOR[session.status]}>{session.status}</Tag>
              <ReportDocumentActions
                reportKey="competency-training-attendance"
                params={{ id: session.id }}
                documentId={session.id}
                sendPermissions={[P.trainingManage]}
              />
              {session.evaluation_id && (
                <Button onClick={() => navigate(`${COMPETENCY_BASE}/effectiveness/${session.evaluation_id}`)}>
                  Effectiveness ({session.evaluation_status})
                </Button>
              )}
              {canManage && session.status === 'Planned' && (
                <>
                  <Popconfirm title="Cancel this session?" onConfirm={() => cancel.mutate()}>
                    <Button icon={<StopOutlined />}>Cancel session</Button>
                  </Popconfirm>
                  <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => setCompleting(true)} disabled={!attendees.length}>
                    Complete
                  </Button>
                </>
              )}
            </Space>
          ) : null
        }
      />

      <Card
        title="Training attendance"
        style={{ marginBottom: 16 }}
        extra={
          editable ? (
            <Button type="primary" icon={<SaveOutlined />} loading={saveHeader.isPending} onClick={() => form.submit()}>
              {isNew ? 'Create session' : 'Save'}
            </Button>
          ) : null
        }
      >
        <Form form={form} layout="vertical" disabled={!editable} onFinish={onSave} initialValues={{ trainer_type: 'Internal', session_date: dayjs() }}>
          <Row gutter={12}>
            <Col xs={24} md={10}>
              <Form.Item name="course_id" label="Training course" rules={[{ required: true }]} extra="Catalog course linked to this attendance.">
                <Select
                  showSearch
                  optionFilterProp="label"
                  disabled={!isNew}
                  options={courses.map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))}
                  onChange={(cid) => {
                    const c = courses.find((x) => x.id === cid);
                    if (c && !form.getFieldValue('topic')) form.setFieldsValue({ topic: c.content || c.title });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="topic"
                label="Training subject(s) / topic(s)"
                rules={[{ required: true, message: 'Enter the subject / topic' }]}
              >
                <Input.TextArea rows={2} placeholder="Subject(s) / topic(s) covered in this session" />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="session_date" label="Date of training" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={6} md={3}>
              <Form.Item name="start_time" label="Time from">
                <Input placeholder="09:00" />
              </Form.Item>
            </Col>
            <Col xs={6} md={3}>
              <Form.Item name="end_time" label="Time to">
                <Input placeholder="11:00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={7}>
              <Form.Item name="location" label="Training location">
                <Input placeholder="Venue / location" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="trainer_type" label="Trainer type">
                <Radio.Group options={[{ value: 'Internal', label: 'Internal' }, { value: 'External', label: 'External' }]} optionType="button" />
              </Form.Item>
            </Col>
            {trainerType === 'External' ? (
              <>
                <Col xs={24} md={9}>
                  <Form.Item name="trainer_name" label="Trainer name" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={9}>
                  <Form.Item name="trainer_organisation" label="Organisation">
                    <Input />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <Col xs={24} md={18}>
                <Form.Item name="trainer_employee_id" label="Trainer name" rules={[{ required: true }]}>
                  <Select showSearch optionFilterProp="label" options={employees.map((e) => ({ value: e.id, label: employeeLabel(e) }))} />
                </Form.Item>
              </Col>
            )}
            {editable && !isNew && (
              <Col xs={24}>
                <Form.Item
                  name="trainer_observations"
                  label="Trainer observations / comments"
                  extra="Printed in the trainer section of the attendance form."
                >
                  <Input.TextArea rows={3} placeholder="List any observations and/or comments" />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
        {isNew && (
          <>
            <Text strong style={{ display: 'block', margin: '8px 0' }}>
              Attendees (optional now, can be added later)
            </Text>
            {attendeePicker}
          </>
        )}
      </Card>

      {!isNew && session && (
        <>
          <Card
            title={`Attendees (${attendees.length})`}
            style={{ marginBottom: 16 }}
            extra={
              session.status === 'Planned' && canManage ? (
                <Space>
                  <Button
                    onClick={() => setMarks(Object.fromEntries(attendees.map((a) => [a.id, 'Present'])))}
                    disabled={!attendees.length}
                  >
                    Mark all present
                  </Button>
                  <Button
                    type="primary"
                    disabled={!dirtyMarks.length}
                    loading={saveAttendance.isPending}
                    onClick={() => saveAttendance.mutate(dirtyMarks.map((a) => ({ id: a.id, attendance_status: marks[a.id] })))}
                  >
                    Save attendance ({dirtyMarks.length})
                  </Button>
                </Space>
              ) : null
            }
          >
            {session.status === 'Planned' && canManage && (
              <div style={{ marginBottom: 12, padding: 12, background: '#fafafa', borderRadius: 6 }}>
                {attendeePicker}
                <Button
                  style={{ marginTop: 12 }}
                  icon={<UsergroupAddOutlined />}
                  disabled={!selectorCount}
                  loading={addAttendees.isPending}
                  onClick={async () => {
                    await addAttendees.mutateAsync(selector);
                    setSelector({});
                  }}
                >
                  Add attendees
                </Button>
              </div>
            )}
            <Table rowKey="id" size="small" pagination={false} dataSource={attendees} columns={attendeeColumns} />
          </Card>
          {session.status !== 'Planned' && session.trainer_observations && (
            <Card title="Trainer observations" style={{ marginBottom: 16 }}>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{session.trainer_observations}</Text>
            </Card>
          )}
          <EntityAttachmentsCard
            entityType={DOCUMENT_ENTITY_TYPE_TRAINING_SESSION}
            entityId={session.id}
            title="Signed attendance sheet and material"
            readOnly={!canManage}
          />
        </>
      )}

      <Modal
        title="Complete training session"
        open={completing}
        onCancel={() => setCompleting(false)}
        okText="Complete"
        confirmLoading={complete.isPending}
        onOk={async () => {
          if (dirtyMarks.length) await saveAttendance.mutateAsync(dirtyMarks.map((a) => ({ id: a.id, attendance_status: marks[a.id] })));
          await complete.mutateAsync(observations);
          setCompleting(false);
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Training records are written for every attendee, and the schedule lines are marked achieved.
            {Number(session?.effectiveness_required) ? ' An effectiveness evaluation (due within 3 months) is opened for the trainees who were present.' : ''}
          </Text>
          {attendees.some((a) => !(marks[a.id] || a.attendance_status)) && (
            <Alert type="warning" showIcon message="Mark attendance for every attendee first." />
          )}
          <Text strong>Trainer observations</Text>
          <Input.TextArea rows={4} value={observations} onChange={(e) => setObservations(e.target.value)} />
          <Text type="secondary">Date: {formatDate(session?.session_date)}</Text>
        </Space>
      </Modal>
    </div>
  );
};

export default TrainingSessionDetailPage;
